import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, TrendingUp, Users, ShoppingBag, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS = {
  new_lead: 'gray', contacted: 'blue', meeting_confirmed: 'blue',
  measurements_taken: 'amber', order_confirmed: 'amber',
  in_production: 'gold', quality_check: 'gold',
  shipped: 'green', delivered: 'green', cancelled: 'red'
}

const STATUS_LABELS = {
  new_lead: 'New Lead', contacted: 'Contacted', meeting_confirmed: 'Meeting Set',
  measurements_taken: 'Measured', order_confirmed: 'Confirmed',
  in_production: 'In Production', quality_check: 'Quality Check',
  shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled'
}

export default function Dashboard() {
  const { profile } = useAuth()
  const [stats, setStats] = useState({ clients: 0, active_orders: 0, revenue: 0, pending_invoices: 0 })
  const [recentOrders, setRecentOrders] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboard()
  }, [])

  async function fetchDashboard() {
    const [
      { count: clientCount },
      { data: orders },
      { data: invoices }
    ] = await Promise.all([
      supabase.from('clients').select('*', { count: 'exact', head: true }),
      supabase.from('orders').select('*, clients(first_name, last_name)').order('created_at', { ascending: false }).limit(5),
      supabase.from('invoices').select('total, status').eq('status', 'sent')
    ])

    const activeOrders = orders?.filter(o => !['delivered','cancelled'].includes(o.status)) || []
    const pendingRevenue = invoices?.reduce((sum, i) => sum + (i.total || 0), 0) || 0

    setStats({
      clients: clientCount || 0,
      active_orders: activeOrders.length,
      revenue: pendingRevenue,
      pending_invoices: invoices?.length || 0
    })
    setRecentOrders(orders || [])
    setLoading(false)
  }

  if (loading) return <div className="empty-state"><div className="splash-logo" style={{fontSize:'32px',color:'var(--gold)'}}>J</div></div>

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700' }}>
          Good {getTimeGreeting()}, {profile?.full_name?.split(' ')[0] || 'there'} 👋
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
          {new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
        </p>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card gold-card">
          <div className="stat-value">{stats.clients.toLocaleString()}</div>
          <div className="stat-label">Total Clients</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--amber)' }}>{stats.active_orders}</div>
          <div className="stat-label">Active Orders</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--blue)', fontSize: '22px' }}>
            ${stats.revenue.toLocaleString()}
          </div>
          <div className="stat-label">Outstanding Invoices</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--red)' }}>{stats.pending_invoices}</div>
          <div className="stat-label">Unpaid Invoices</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header"><span className="card-title">Quick Actions</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Link to="/orders/new" className="btn btn-primary" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <ShoppingBag size={20} />
            <span style={{ fontSize: '13px' }}>New Order</span>
          </Link>
          <Link to="/clients/new" className="btn btn-outline" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <Plus size={20} />
            <span style={{ fontSize: '13px' }}>New Client</span>
          </Link>
          <Link to="/fabrics/new" className="btn btn-outline" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <Plus size={20} />
            <span style={{ fontSize: '13px' }}>Add Fabric</span>
          </Link>
          <Link to="/orders/scan" className="btn btn-outline" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <span style={{ fontSize: '22px' }}>📷</span>
            <span style={{ fontSize: '13px' }}>Scan Sheet</span>
          </Link>
          <Link to="/invoices/quick" className="btn btn-gold" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <span style={{ fontSize: '22px' }}>⚡</span>
            <span style={{ fontSize: '13px' }}>Quick Invoice</span>
          </Link>
          <Link to="/invoices/new" className="btn btn-outline" style={{ borderRadius: '10px', padding: '14px', flexDirection: 'column', gap: '6px', height: '72px' }}>
            <Plus size={20} />
            <span style={{ fontSize: '13px' }}>New Invoice</span>
          </Link>
        </div>
      </div>

      {/* Recent orders */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Recent Orders</span>
          <Link to="/orders" style={{ fontSize: '13px', color: 'var(--gold)', textDecoration: 'none' }}>See all</Link>
        </div>
        {recentOrders.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px' }}>
            <ShoppingBag size={32} style={{ marginBottom: '8px' }} />
            <p className="empty-state-title">No orders yet</p>
            <p className="empty-state-sub">Add your first order to get started</p>
          </div>
        ) : (
          <div style={{ margin: '0 -16px' }}>
            {recentOrders.map(order => (
              <Link key={order.id} to={`/orders/${order.id}`} className="list-item">
                <div className="list-item-avatar" style={{ borderRadius: '8px', fontSize: '12px', width: '38px', height: '38px' }}>
                  {order.garment_type?.substring(0,2).toUpperCase()}
                </div>
                <div className="list-item-content">
                  <div className="list-item-title">{order.order_number}</div>
                  <div className="list-item-sub">
                    {order.clients ? `${order.clients.first_name || ''} ${order.clients.last_name || ''}`.trim() : 'Unknown client'}
                    {' · '}{order.garment_type}
                  </div>
                </div>
                <div className="list-item-right">
                  <span className={`badge badge-${STATUS_COLORS[order.status] || 'gray'}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                  {order.price && (
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                      {order.currency} {Number(order.price).toLocaleString()}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function getTimeGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 17) return 'afternoon'
  return 'evening'
}
